/**
 * A reporting label over several general ledger accounts (PAUL-3624), so a figure no single account
 * holds — "daily F&B revenue" — can be read as one number. Company-scoped, name unique per company.
 * Deliberately has no code and no active flag: a group is a label, not a ledger account.
 */
export interface GeneralLedgerAccountGroupModel {
  id: string;
  company_id: string;
  name?: string | null;
  description?: string | null;
  /** One kind per group, optional (PAUL-3845). Edit replaces it, so always send it, null included. */
  revenue_kind?: RevenueKind | null;
  /** Non-deleted accounts currently assigned. Read-only — set by the list and detail projections only. */
  accounts_count?: number;
}

/**
 * The kind of revenue a group holds, one list for every company (PAUL-3845). The numbers mirror the backend and
 * are frozen: new kinds append (7, 8, ...) and Other stays 99.
 */
export enum RevenueKind {
  FoodAndBeverage = 1,
  Tickets = 2,
  SurchargesAndFees = 3,
  Merchandise = 4,
  Subscriptions = 5,
  RentalAndEvents = 6,
  Other = 99,
}

/** Display order: Other last, whatever is appended. */
export const REVENUE_KINDS: RevenueKind[] = [
  RevenueKind.FoodAndBeverage,
  RevenueKind.Tickets,
  RevenueKind.SurchargesAndFees,
  RevenueKind.Merchandise,
  RevenueKind.Subscriptions,
  RevenueKind.RentalAndEvents,
  RevenueKind.Other,
];

export const RevenueKindTranslations: Record<RevenueKind, string> = {
  [RevenueKind.FoodAndBeverage]: 'general_ledgers.groups.revenue_kinds.food_and_beverage',
  [RevenueKind.Tickets]: 'general_ledgers.groups.revenue_kinds.tickets',
  [RevenueKind.SurchargesAndFees]: 'general_ledgers.groups.revenue_kinds.surcharges_and_fees',
  [RevenueKind.Merchandise]: 'general_ledgers.groups.revenue_kinds.merchandise',
  [RevenueKind.Subscriptions]: 'general_ledgers.groups.revenue_kinds.subscriptions',
  [RevenueKind.RentalAndEvents]: 'general_ledgers.groups.revenue_kinds.rental_and_events',
  [RevenueKind.Other]: 'general_ledgers.groups.revenue_kinds.other',
};

export const REVENUE_KIND_NOT_SET = 'general_ledgers.groups.revenue_kind_not_set';

/** Translation key for a group's kind; a number this build does not know yet is shown as is. */
export function revenueKindTranslationKey(kind: RevenueKind | null | undefined): string {
  if (kind === null || kind === undefined) {
    return REVENUE_KIND_NOT_SET;
  }

  return RevenueKindTranslations[kind] ?? String(kind);
}
